import type { ClipboardEventHandler, RefObject } from "react";

import { createValueFragment, readValue } from "./utils";

import type { MentionOption } from "./types";

type UseMentionClipboardParams = {
  editorRef: RefObject<HTMLDivElement>;
  mentions: MentionOption[];
  mask?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onChange: (value: string) => void;
  closeSuggestions: () => void;
};

const useMentionClipboard = (params: UseMentionClipboardParams) => {
  const {
    editorRef,
    mentions,
    mask,
    disabled,
    readOnly,
    onChange,
    closeSuggestions
  } = params;

  const copySelectionToClipboard = (
    event: Parameters<ClipboardEventHandler<HTMLDivElement>>[0]
  ) => {
    const editor = editorRef.current;
    const selection = window.getSelection();

    if (!editor || !selection?.rangeCount) {
      return null;
    }

    const range = selection.getRangeAt(0);

    if (
      range.collapsed ||
      !editor.contains(range.startContainer) ||
      !editor.contains(range.endContainer)
    ) {
      return null;
    }

    const container = document.createElement("div");
    container.append(range.cloneContents());
    event.preventDefault();
    event.clipboardData.setData("text/plain", readValue(container));

    return range;
  };

  const handleCopy: ClipboardEventHandler<HTMLDivElement> = event => {
    copySelectionToClipboard(event);
  };

  const handleCut: ClipboardEventHandler<HTMLDivElement> = event => {
    const editor = editorRef.current;

    if (!editor || disabled || readOnly) {
      return;
    }

    const range = copySelectionToClipboard(event);

    if (!range) {
      return;
    }

    range.deleteContents();
    onChange(readValue(editor));
    closeSuggestions();
  };

  const handlePaste: ClipboardEventHandler<HTMLDivElement> = event => {
    const editor = editorRef.current;
    const selection = window.getSelection();

    if (
      !editor ||
      disabled ||
      readOnly ||
      !selection?.rangeCount ||
      !editor.contains(selection.getRangeAt(0).startContainer) ||
      !editor.contains(selection.getRangeAt(0).endContainer)
    ) {
      return;
    }

    event.preventDefault();

    const range = selection.getRangeAt(0);
    const fragment = createValueFragment(
      event.clipboardData.getData("text/plain"),
      mentions,
      mask
    );
    const lastInsertedNode = fragment.lastChild;

    range.deleteContents();
    range.insertNode(fragment);

    if (lastInsertedNode) {
      range.setStartAfter(lastInsertedNode);
    }

    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    onChange(readValue(editor));
    closeSuggestions();
  };

  return { handleCopy, handleCut, handlePaste };
};

export default useMentionClipboard;
